import React, { Children, useState, useEffect, useMemo, useRef } from 'react';
import { node, number } from 'prop-types';

import IconChevronRight from '@/assets/Icon/svg/IconChevronRight';
import { throttle } from '@/utils';
import { N200 } from '@/colors/colorsV1';

import { styNavButton, styListContainer, styLeftIcon, styChildContainer } from './style';

const throttleTimeInMilisecond = 300;

export default function SideCarouselContainer({ children, childWidth, spaceBetweenElements }) {
  const containerRefs = useRef(null);
  const [activeChildIndex, setActiveChildIndex] = useState(0);

  const containerRef = containerRefs?.current;

  useEffect(() => {
    if (containerRef) {
      containerRefs.current.scroll({
        left: (childWidth + spaceBetweenElements) * activeChildIndex,
        behavior: 'smooth',
      });
    }
  }, [containerRef, childWidth, spaceBetweenElements, activeChildIndex]);

  const throttledSetPrevActiveChildIndex = useMemo(
    () =>
      throttle(
        () => setActiveChildIndex((prevActiveIndex) => prevActiveIndex - 1),
        throttleTimeInMilisecond
      ),
    []
  );

  const throttledSetNextActiveChildIndex = useMemo(
    () =>
      throttle(
        () => setActiveChildIndex((prevActiveIndex) => prevActiveIndex + 1),
        throttleTimeInMilisecond
      ),
    []
  );

  const onClickLeftNavButton = () => {
    const firstChildPosition = containerRef?.firstChild?.getBoundingClientRect();
    const currentContainerPosition = containerRefs?.current?.getBoundingClientRect();

    if (firstChildPosition?.left !== currentContainerPosition.left) {
      throttledSetPrevActiveChildIndex();
    }
  };

  const onClickRightNavButton = () => {
    const lastChildPosition = containerRef?.lastChild?.getBoundingClientRect();
    const containerPosition = containerRef?.getBoundingClientRect();

    if (lastChildPosition?.right > containerPosition?.right) {
      throttledSetNextActiveChildIndex();
    }
  };

  return (
    <div style={{ display: 'flex' }} data-testid="sideCarouselContainer">
      <div
        role="button"
        className={styLeftIcon}
        onClick={onClickLeftNavButton}
        data-testid="sideCarouselLeftNavButton"
      >
        <IconChevronRight color={N200} size={15} />
      </div>
      <div ref={containerRefs} className={styListContainer}>
        {Children.map(children, (child, i) => (
          <div
            role="button"
            className={styChildContainer(childWidth, spaceBetweenElements)}
            key={i}
          >
            {child}
          </div>
        ))}
      </div>
      <div
        role="button"
        className={styNavButton}
        onClick={onClickRightNavButton}
        data-testid="sideCarouselRightNavButton"
      >
        <IconChevronRight color={N200} size={15} />
      </div>
    </div>
  );
}

SideCarouselContainer.propTypes = {
  children: node.isRequired,
  childWidth: number,
  spaceBetweenElements: number,
};

SideCarouselContainer.defaultProps = {
  childWidth: 280,
  spaceBetweenElements: 16,
};
